import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  ShoppingCart,
  ClipboardList,
  BarChart2,
  Wrench,
  Database,
  Users,
  Lock,
  Zap,
  RefreshCw,
  Tag,
  AlertCircle,
  ArrowRight,
  Truck,
  Building2,
  HardHat,
  Factory,
  Globe,
  Briefcase,
  Shield,
  BookOpen,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

// ─────────────────────────────────────────────────────────────
// DATA (verbatim from desktop)
// ─────────────────────────────────────────────────────────────

const stages = [
  {
    id: "design",
    num: "01",
    label: "Design Management",
    sub: "Every product starts with a spec.",
    Icon: FileText,
    color: "from-sky-500 to-cyan-400",
    accent: "sky",
    accentHex: "#0EA5E9",
    lightBg: "bg-sky-50",
    lightBorder: "border-sky-200",
    dotColor: "bg-sky-500",
    summary:
      "Engineering owns the technical content. Procurement tracks it, logs it, and ensures it translates to the project level. Design Management is the single source of truth for every product spec — SKUs, logistics details, storage requirements, nameplate photos, and the living record of every design change after approval. When a design changes, the downstream ripple — across commissioning checklists, spares, field mods — is visible before it becomes a problem.",
    bullets: [
      "Program documentation: upload, date-stamp, approval status per revision",
      "Design change tracking after approval — field mods, safety redesigns, Ops redesigns",
      "Logistics library: weight, dims, pallet count, truck count, packaging photos",
      "Storage requirements defined at the program level",
      "Quality system link: open SCARs and alerts surface on every vendor-product lookup",
      "Region-specific and AHJ requirements repository",
      "Tenant-specific and program-specific design requirement files",
    ],
  },
  {
    id: "buyout",
    num: "02",
    label: "Buyout",
    sub: "Turning plans into purchase orders.",
    Icon: ShoppingCart,
    color: "from-emerald-500 to-teal-400",
    accent: "emerald",
    accentHex: "#10B981",
    lightBg: "bg-emerald-50",
    lightBorder: "border-emerald-200",
    dotColor: "bg-emerald-500",
    summary:
      "The OFE Plan from business leadership — megawatts, blocks, program, priority — becomes the input that drives everything downstream. Oracle POs capture the commercial reality: vendor, line, quantity, unit price, expenditure type, requested delivery. The gap today is that Oracle lines are entered manually, often with quantities greater than one, making unit-price analysis across all cost types nearly impossible. The future system explodes every multi-unit line automatically and ensures every line — freight, services, storage, change orders — carries a project allocation.",
    bullets: [
      "OFE Plan inputs: MW, blocks, design program, priority order drive system quantities",
      "Auto-populate Oracle PO data into Production Planners — no manual re-entry",
      "Smart line explosion: QTY > 1 on a PO line splits to one row per unit",
      "Unit prices enforced across all cost types: product, freight, services, miscellaneous",
      "Tax-applicability flag per project — never forgotten on a taxable PO",
      "Vendor Profiles: contacts, org chart, MSA tracking, milestone payments, quality assessments",
      "Auto-file every PO copy to a designated folder on creation",
    ],
  },
  {
    id: "production",
    num: "03",
    label: "Production Planning",
    sub: "One row. One asset. Full visibility.",
    Icon: ClipboardList,
    color: "from-violet-500 to-fuchsia-500",
    accent: "violet",
    accentHex: "#8B5CF6",
    lightBg: "bg-violet-50",
    lightBorder: "border-violet-200",
    dotColor: "bg-violet-500",
    summary:
      "This is where each product becomes its own asset. Every unit — quantity one per row — is tracked from manufacturing through first delivery. Vendors can access their own planner to update production status, ship dates, and serial numbers. Internal teams see on-time performance, storage addresses, and contractual dates. When a project gets reallocated, the system tracks the history. When a vendor updates a field, access controls ensure they only touch what they're permitted to touch.",
    bullets: [
      "QTY 1 per row — every unit is an individually tracked asset from day one",
      "Vendor-accessible: controlled by vendor, by planner, and by data column",
      "Production status, MFG ship date (plan vs actual), on-time performance",
      "Vendor storage tracking: address, out-of-storage date, first delivery destination",
      "Serial number assignment and capture at the unit level",
      "Contractual date vs requested delivery — gap always visible",
      "Access control by internal ADC team — not every team sees every planner",
    ],
  },
  {
    id: "project",
    num: "04",
    label: "Project Management",
    sub: "Schedule, dollars, and documents — unified.",
    Icon: BarChart2,
    color: "from-amber-500 to-orange-400",
    accent: "amber",
    accentHex: "#F59E0B",
    lightBg: "bg-amber-50",
    lightBorder: "border-amber-200",
    dotColor: "bg-amber-500",
    summary:
      "Project Management pulls from Production Planners and Oracle in real time. The Project Schedule auto-populates from vendor allocations. Project Financials roll up every PO line by project and PFR code, with a manual exposure layer for PM-entered risk costs. Submittals tracked internally with SharePoint links — not locked inside a GC-controlled Procore. Contacts drawn from the central Vendor Profile database. Storage Locations tracked from 3PL to integrator to GC. And AI determines which costs need a Journal Entry to move between projects — and flags when the move is complete.",
    bullets: [
      "Project Schedule auto-filled from Production Planner allocations",
      "Reallocation history tracked — FinCode detail replaced by system audit trail",
      "Project Financials: PO + exposure subtotals, PFR code rollup, by-project view",
      "AI-assisted Journal Entry identification: which line, which project, which cost",
      "Submittals tracked internally — ADC-owned, not GC-gated",
      "Storage Locations database: type, address, POC, hours, insurance, warehouse ID",
      "Project Contacts: vendors from profiles, GC, EC, MC, EOR, CxA — all linked",
    ],
  },
  {
    id: "cx",
    num: "05",
    label: "CX & Field Scope",
    sub: "The last mile: from vendor to installation.",
    Icon: Wrench,
    color: "from-rose-500 to-pink-400",
    accent: "rose",
    accentHex: "#F43F5E",
    lightBg: "bg-rose-50",
    lightBorder: "border-rose-200",
    dotColor: "bg-rose-500",
    summary:
      "Procurement hands off to Construction — but the equipment's story doesn't end there. The future system tracks from GC storage through installation, commissioning stages, and turnover to Ops. CxAlloy becomes a data feed, not a silo. The Cx Look Ahead — a monthly report to all vendors showing project phase timelines — backfeeds into Production Planners so vendors always know where their equipment is heading. The 3PL Inventory Management System syncs movement data automatically. Serial number is the link across every handoff.",
    bullets: [
      "Post-delivery status: GC storage → installation → Cx stages → Ops turnover",
      "CxAlloy integration: installation and beyond status feeds back into the system",
      "Cx Look Ahead: monthly vendor-facing schedule by project, phase, and L2–RFS milestones",
      "Look Ahead data backfeeds Production Planners — vendors always see field timing",
      "3PL sync: product movement after delivery automated, no manual updates",
      "Serial number as the universal link from manufacture to operations",
      "OFE Cx Playbook: potential for AI-assisted sequencing and schedule automation",
    ],
  },
];

const systemPillars = [
  {
    Icon: Lock,
    color: "from-rose-500 to-pink-500",
    title: "Access Control — Paramount",
    body: "User type governs everything. Vendors see only their planner, only permitted columns. Internal teams are scoped by function. External contractors get what they need and nothing more. This is the non-negotiable foundation of the entire system.",
  },
  {
    Icon: Zap,
    color: "from-sky-500 to-indigo-500",
    title: "Oracle Auto-Population",
    body: "Today, Procurement re-enters Oracle PO data by hand into every Production Planner. The future system reads Oracle directly — and when a line has QTY > 1, it explodes it automatically into individual asset rows. No humans transcribing numbers.",
  },
  {
    Icon: Tag,
    color: "from-violet-500 to-fuchsia-500",
    title: "Unit Prices Across All Costs",
    body: "Product enters Oracle at unit price. Freight, services, and storage often enter lump-sum as QTY 1. The system enforces unit-price capture universally — because you cannot manage cost exposure you cannot see at the line level.",
  },
  {
    Icon: RefreshCw,
    color: "from-emerald-500 to-teal-400",
    title: "Live Reallocation Tracking",
    body: "When equipment moves from one project to another, every linked field — PO line, cost, schedule row — moves with it. The history of every reallocation is preserved automatically. No more FinCode spreadsheets maintained by hand.",
  },
  {
    Icon: Database,
    color: "from-amber-500 to-orange-400",
    title: "Serial Number as the Spine",
    body: "From the Production Planner to CxAlloy to the 3PL system, serial number is the universal key. Every handoff, every status change, every document attachment traces back to one unique asset identifier. Equipment never gets lost between systems again.",
  },
  {
    Icon: AlertCircle,
    color: "from-cyan-500 to-sky-400",
    title: "AI-Assisted Financials",
    body: "The system uses AI to identify which PO line costs need a Journal Entry to move between projects — and tracks when the entry is complete. Tax applicability flags prevent forgotten tax charges. Exposure costs added by PMs sit alongside actuals in every financial view.",
  },
];

const userTypes = [
  { label: "Procurement",       color: "text-violet-700",  bg: "bg-violet-50",  border: "border-violet-200",  live: true  },
  { label: "Engineering",       color: "text-sky-700",     bg: "bg-sky-50",     border: "border-sky-200",     live: true  },
  { label: "Project Mgmt",      color: "text-amber-700",   bg: "bg-amber-50",   border: "border-amber-200",   live: true  },
  { label: "Vendors",           color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", live: true  },
  { label: "General Contractor",color: "text-orange-700",  bg: "bg-orange-50",  border: "border-orange-200"               },
  { label: "Integrators",       color: "text-teal-700",    bg: "bg-teal-50",    border: "border-teal-200"                 },
  { label: "3PL Warehouses",    color: "text-blue-700",    bg: "bg-blue-50",    border: "border-blue-200"                 },
  { label: "CxA",               color: "text-pink-700",    bg: "bg-pink-50",    border: "border-pink-200"                 },
  { label: "Electrical Contractor", color: "text-indigo-700", bg: "bg-indigo-50", border: "border-indigo-200"            },
  { label: "EOR",               color: "text-red-700",     bg: "bg-red-50",     border: "border-red-200"                  },
  { label: "Operations",        color: "text-slate-700",   bg: "bg-slate-50",   border: "border-slate-200"                },
];

const connectedSystems = [
  {
    Icon: Database,
    color: "text-orange-600",
    bg: "bg-orange-50",
    iconBorder: "border-orange-200",
    label: "Oracle ERP",
    blurb: "The system of record for POs. Future state: auto-reads every line, explodes quantities, allocates every cost to a project — no Procurement re-entry.",
  },
  {
    Icon: Factory,
    color: "text-violet-600",
    bg: "bg-violet-50",
    iconBorder: "border-violet-200",
    label: "CxAlloy",
    blurb: "Commissioning and field status feeds back into the procurement system by serial number — closing the loop from purchase order to operational asset.",
  },
  {
    Icon: Truck,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    iconBorder: "border-emerald-200",
    label: "3PL Systems",
    blurb: "Inventory movement data synced automatically after delivery. The moment product leaves the warehouse, the ADC system knows — without a phone call.",
  },
  {
    Icon: Shield,
    color: "text-blue-600",
    bg: "bg-blue-50",
    iconBorder: "border-blue-200",
    label: "ServiceNow",
    blurb: "Quality events, SCARs, and service requests link to Vendor Profiles and Design Management — so anyone looking up a vendor sees the full quality picture.",
  },
  {
    Icon: Globe,
    color: "text-sky-600",
    bg: "bg-sky-50",
    iconBorder: "border-sky-200",
    label: "SharePoint",
    blurb: "Submittals filed internally, not locked in GC-controlled Procore. ADC owns the document trail from first submittal through O&M handover.",
  },
  {
    Icon: BookOpen,
    color: "text-pink-600",
    bg: "bg-pink-50",
    iconBorder: "border-pink-200",
    label: "Procore / Helix",
    blurb: "GC-managed submittal platforms tracked in parallel — ADC never loses visibility when a GC controls the primary tool.",
  },
];

const lifecycleTags = [
  "Program Design",
  "Vendor Qualification",
  "PO Issuance",
  "Manufacturing",
  "Logistics",
  "First Delivery",
  "GC Storage",
  "Installation",
  "Commissioning",
  "Ops Turnover",
];

// ─────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────

function SectionLabel({ index, children }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400 mb-2">
      {String(index).padStart(2, "0")} / {children}
    </p>
  );
}

function AccordionStage({ stage, expanded, onToggle }) {
  const { Icon } = stage;
  const isOpen = expanded === stage.id;

  return (
    <div className="mb-3">
      <button
        onClick={() => onToggle(isOpen ? null : stage.id)}
        className={`w-full rounded-2xl border p-4 text-left transition-all duration-200 min-h-[56px] ${
          isOpen
            ? "border-zinc-900 bg-zinc-900 text-white shadow-xl dark:border-white dark:bg-white dark:text-zinc-900"
            : "border-zinc-200 bg-white/80 hover:border-zinc-300 dark:border-zinc-800 dark:bg-zinc-900/70"
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            className={`shrink-0 rounded-xl bg-gradient-to-br ${stage.color} p-2.5 text-white shadow-lg`}
          >
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div
              className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${
                isOpen ? "opacity-50" : "text-zinc-400"
              }`}
            >
              {stage.num}
            </div>
            <div className="text-[15px] font-semibold leading-tight">
              {stage.label}
            </div>
            <p
              className={`mt-0.5 text-xs leading-relaxed ${
                isOpen ? "text-zinc-300 dark:text-zinc-600" : "text-zinc-500"
              }`}
            >
              {stage.sub}
            </p>
          </div>
          {isOpen ? (
            <ChevronUp className="h-5 w-5 shrink-0 opacity-50" />
          ) : (
            <ChevronDown className="h-5 w-5 shrink-0 opacity-30" />
          )}
        </div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="pt-3 px-1">
              <p className="mb-4 text-sm leading-7 text-zinc-600 dark:text-zinc-300">
                {stage.summary}
              </p>
              <div className="flex flex-col gap-2">
                {stage.bullets.map((item) => (
                  <div
                    key={item}
                    className={`flex items-start gap-3 rounded-2xl border ${stage.lightBorder} ${stage.lightBg} p-3 dark:border-zinc-800 dark:bg-zinc-900/50`}
                  >
                    <div
                      className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${stage.dotColor}`}
                    />
                    <p className="text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────

export default function ProcurementStoryMobile() {
  const [expandedStage, setExpandedStage] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Show back-to-top after scrolling down
  React.useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 600);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 via-white to-zinc-100 text-zinc-900 dark:from-zinc-950 dark:via-zinc-950 dark:to-zinc-900 dark:text-zinc-100">
      <div className="px-4 pb-24">

        {/* ── HERO ─────────────────────────────────────────────── */}
        <section className="py-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Badge className="mb-4 rounded-full px-3 py-1.5 text-xs font-semibold tracking-wide">
              From design spec to operational asset
            </Badge>
            <h1 className="text-3xl font-bold leading-[1.15] tracking-tight">
              ADC Procurement —{" "}
              <span className="text-zinc-400">one system,</span>
              <br />
              every handoff.
            </h1>
            <p className="mt-4 text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Today, almost every data point that moves between Design, Buyout,
              Production, and the Field is a manual entry. The future system
              connects all five domains — so data flows once, and every team
              always sees the truth.
            </p>
          </motion.div>

          {/* Core thesis card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="mt-6"
          >
            <Card className="rounded-2xl border-0 bg-zinc-900 text-white shadow-2xl dark:bg-zinc-900">
              <CardContent className="p-5">
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">
                  Core thesis
                </p>
                <p className="mb-3 text-sm leading-7 text-zinc-300">
                  User access control is paramount. Vendors see only their data.
                  Teams see only what they need. Every unit of equipment is
                  tracked as its own asset — from manufacture to operations —
                  with serial number as the universal key.
                </p>
                <p className="text-sm leading-7 text-zinc-300">
                  The same connected system that auto-populates Production
                  Planners from Oracle also feeds the Project Schedule, rolls up
                  financials by PFR code, and links quality events to every
                  vendor-product lookup.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* ── 01 / FIVE DOMAINS (Accordion) ───────────────────── */}
        <section className="py-8">
          <div className="mb-6">
            <SectionLabel index={1}>The Five Domains</SectionLabel>
            <h2 className="text-2xl font-bold tracking-tight">
              Start with design. End with operations.
            </h2>
            <p className="mt-2 text-sm leading-7 text-zinc-500 dark:text-zinc-400">
              Five connected domains, one system. Each domain feeds the next.
              Data entered once propagates forward — no re-keying, no
              version drift, no lost handoffs.
            </p>
          </div>

          <div>
            {stages.map((stage) => (
              <AccordionStage
                key={stage.id}
                stage={stage}
                expanded={expandedStage}
                onToggle={setExpandedStage}
              />
            ))}
          </div>
        </section>

        {/* ── 02 / SYSTEM PILLARS ──────────────────────────────── */}
        <section className="py-8">
          <div className="mb-6">
            <SectionLabel index={2}>System Design Principles</SectionLabel>
            <h2 className="text-2xl font-bold tracking-tight">
              Six rules the system never breaks.
            </h2>
            <p className="mt-2 text-sm leading-7 text-zinc-500 dark:text-zinc-400">
              These aren't features — they're the structural commitments that make
              every other capability trustworthy. Get these wrong and the whole
              system collapses back into spreadsheets.
            </p>
          </div>

          <div className="flex flex-col gap-4">
            {systemPillars.map((pillar) => {
              const { Icon } = pillar;
              return (
                <motion.div
                  key={pillar.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  viewport={{ once: true }}
                >
                  <Card className="rounded-2xl border border-zinc-100 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900/70">
                    <CardContent className="p-5">
                      <div
                        className={`mb-4 inline-flex rounded-xl bg-gradient-to-br ${pillar.color} p-2.5 text-white shadow-lg`}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="mb-2 text-base font-bold text-zinc-900 dark:text-zinc-100">
                        {pillar.title}
                      </h3>
                      <p className="text-sm leading-7 text-zinc-500 dark:text-zinc-400">
                        {pillar.body}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* ── 03 / USER TYPES ──────────────────────────────────── */}
        <section className="py-8">
          <div className="mb-6">
            <SectionLabel index={3}>Who Uses It</SectionLabel>
            <h2 className="text-2xl font-bold tracking-tight">
              One system, eleven stakeholders.
            </h2>
            <p className="mt-2 text-sm leading-7 text-zinc-500 dark:text-zinc-400">
              Access control is the foundation — not an afterthought. Every
              user type has a defined scope: what they can see, what they can
              edit, and which projects they can touch.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {userTypes.map((v) => (
              <span
                key={v.label}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold ${v.color} ${v.bg} ${v.border}`}
              >
                {v.live && (
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${v.color.replace("text-", "bg-")}`}
                  />
                )}
                {v.label}
                {v.live && (
                  <span className="ml-0.5 text-[10px] font-bold opacity-60">
                    INTERNAL
                  </span>
                )}
              </span>
            ))}
          </div>
          <div className="mt-4 flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-violet-500" />
              <span className="text-xs text-zinc-400">
                Internal = ADC team with full or role-scoped access
              </span>
            </div>
            <span className="ml-4 text-xs text-zinc-300">
              All others: external access with column-level and project-level controls
            </span>
          </div>
        </section>

        {/* ── 04 / CONNECTED VISION ────────────────────────────── */}
        <section className="py-8">
          <div className="mb-6">
            <SectionLabel index={4}>The Connected System</SectionLabel>
            <h2 className="text-2xl font-bold tracking-tight">
              One spine. Every system reports to it.
            </h2>
            <p className="mt-2 text-sm leading-7 text-zinc-500 dark:text-zinc-400">
              The future procurement platform isn't a standalone tool — it's
              the connective tissue between Oracle, CxAlloy, ServiceNow,
              SharePoint, and every 3PL and GC system in the field.
            </p>
          </div>

          {/* Dark "End State" card — full width on top */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            viewport={{ once: true }}
            className="mb-4"
          >
            <Card className="rounded-2xl border-0 bg-zinc-900 text-white shadow-2xl">
              <CardContent className="p-5">
                <div className="mb-4 inline-flex rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 p-3 text-white shadow-xl">
                  <HardHat className="h-6 w-6" />
                </div>
                <h3 className="mb-3 text-xl font-bold">The End State</h3>
                <p className="mb-3 text-sm leading-7 text-zinc-300">
                  Today: a Procurement team member touches the same data
                  point in Oracle, a Production Planner, a Project Schedule,
                  and a financial report — four times, by hand, every time
                  something changes.
                </p>
                <p className="mb-6 text-sm leading-7 text-zinc-300">
                  The future system: data enters once. Oracle feeds the
                  Planner. The Planner feeds the Schedule. The Schedule
                  feeds the financial view. Serial numbers connect every
                  system from manufacture to Ops. And AI handles the rest.
                </p>

                {/* Lifecycle tags */}
                <div className="border-t border-white/10 pt-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
                    Full lifecycle coverage
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {lifecycleTags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-xl border border-white/10 bg-white/[0.07] px-3 py-1 text-xs text-zinc-300"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Connected systems — single column */}
          <div className="flex flex-col gap-3">
            {connectedSystems.map((item) => {
              const { Icon } = item;
              return (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  viewport={{ once: true }}
                >
                  <Card className="rounded-2xl border border-zinc-100 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900/70">
                    <CardContent className="flex items-start gap-4 p-4">
                      <div
                        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border ${item.iconBorder} ${item.bg} ${item.color}`}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="mb-1 text-sm font-bold text-zinc-900 dark:text-zinc-100">
                          {item.label}
                        </h4>
                        <p className="text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
                          {item.blurb}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>
      </div>

      {/* ── BACK TO TOP ──────────────────────────────────────── */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            onClick={scrollToTop}
            className="fixed bottom-6 right-4 z-50 flex h-11 w-11 items-center justify-center rounded-full bg-zinc-900 text-white shadow-lg active:scale-95 dark:bg-white dark:text-zinc-900"
            aria-label="Back to top"
          >
            <ChevronUp className="h-5 w-5" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
